import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { BellIcon } from '@/components/icons'
import { RECENT_COUNT, notificationListQuery, unreadCountQuery, useReadAll } from '@/features/notifications/api'
import { NotificationRow } from '@/features/notifications/row'
import { HeaderMenu } from '@/features/nav/menu'
import type { System } from '@/lib/auth-store'
import { useT } from '@/lib/i18n/translate'
import { notificationsPathFor } from '@/lib/return-path'

/**
 * Top-bar bell (H3): the unread count polled every minute, and a panel with
 * the newest few notices, "See all" and "Mark all read" (2026-09-13).
 */
export function NotificationBell({ system }: { system: System }) {
  const t = useT()
  const unread = useQuery(unreadCountQuery(system)).data?.unread ?? 0
  return (
    <HeaderMenu
      label={unread > 0 ? t('notification.bellUnread', { count: unread }) : t('notification.bell')}
      icon={
        <span className="relative flex">
          <BellIcon />
          {unread > 0 && (
            <span
              aria-hidden
              className="bg-accent text-accent-fg absolute -top-1.5 -right-2 min-w-4 rounded-full px-1 text-center text-[0.65rem] leading-4 font-semibold"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      }
      // Wider than the option menus; shifted past the avatar so it stays on a phone screen.
      panel="-right-12 w-[min(20rem,calc(100vw-2rem))] sm:right-0"
    >
      {(close) => <Panel system={system} close={close} />}
    </HeaderMenu>
  )
}

/** The rows are fetched on open, so the button follows their `unread`, not the minute-old poll. */
function Panel({ system, close }: { system: System; close: () => void }) {
  const t = useT()
  const recent = useQuery(notificationListQuery(system, 1, RECENT_COUNT))
  const readAll = useReadAll(system)
  const unread = recent.data?.unread ?? 0
  return (
    <div className="flex flex-col gap-1">
      {recent.isPending && <p className="text-muted px-3 py-2 text-sm">{t('common.loading')}</p>}
      {recent.error && (
        <div className="flex flex-col gap-2 px-3 py-2">
          <FormError error={recent.error} />
          <Button variant="secondary" size="sm" onClick={() => void recent.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {recent.data &&
        (recent.data.items.length === 0 ? (
          <p className="text-muted px-3 py-2 text-sm">{t('notification.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {recent.data.items.map((item) => (
              <li key={item.id}>
                <NotificationRow system={system} item={item} compact onOpen={close} />
              </li>
            ))}
          </ul>
        ))}
      <FormError error={readAll.error} className="mx-3" />
      <div className="border-border flex items-center justify-between gap-2 border-t pt-1">
        <Link to={notificationsPathFor(system)} search={{ page: 1 }} onClick={close} className="text-accent-ink hover:bg-accent-soft flex min-h-9 items-center rounded-lg px-3 text-sm font-medium">
          {t('notification.seeAll')}
        </Link>
        {unread > 0 && (
          <Button variant="ghost" size="sm" loading={readAll.isPending} onClick={() => readAll.mutate()}>
            {t('notification.markAllRead')}
          </Button>
        )}
      </div>
    </div>
  )
}
